import { useQuery } from "@tanstack/react-query";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";
import { SUPPORT_REQUESTS_QUERY_KEY, type SupportRequestWithRelations } from "../supportConstants";
import { pendingStatusOverrides } from "./useSupportApi";

export const useSupportRequests = () => {
  const { userId } = useUserRole();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [SUPPORT_REQUESTS_QUERY_KEY],
    enabled: Boolean(userId),
    // A fetch that resolves while a drag&drop status mutation is in flight must not
    // snap the card back — layer the pending status on top (TRE-1765 #1).
    select: (requests: SupportRequestWithRelations[]) =>
      pendingStatusOverrides.size === 0
        ? requests
        : requests.map((request) => {
            const pendingStatus = pendingStatusOverrides.get(request.id);
            return pendingStatus && pendingStatus !== request.status ? { ...request, status: pendingStatus } : request;
          }),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_request")
        .select(
          "*, reporter:reporterUserId(id, email, firstName, lastName), assignee:assigneeUserId(id, email, firstName, lastName, avatarUrl), trainee:traineeId(id, firstName, lastName, email)",
        )
        .order("createdAt", { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []) as unknown as SupportRequestWithRelations[];
    },
  });

  return { requests: data || [], isLoading, error, refetch };
};
